import * as bs from 'bootstrap';
import axios from 'smg/axios';
import * as session from 'smg/customer-api/session';
import { UserTaxExemptions, UpdateUserTaxExemptionLabel } from 'smg/customer-api/usertaxexemptions';
import * as models from 'smg/customer-api/models';
import { ShowError } from 'smg/errors';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import * as taxoptions from '../../usertaxexemptions';

void main();

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    const sess = await session.Session();
    const $mainSection = document.querySelector<HTMLElement>('#tax-exempt-options') as HTMLElement;
    const $defaultTaxOptionParent = $mainSection.querySelector<HTMLElement>('#default-tax-option-parent') as HTMLElement;
    const $defaultTaxOption = $defaultTaxOptionParent.querySelector<HTMLElement>('#default-tax-option') as HTMLElement;
    const $taxOptionsListParent = $mainSection.querySelector<HTMLElement>('#tax-options-list-parent') as HTMLElement;
    const $taxOptionsList = $taxOptionsListParent.querySelector<HTMLElement>('#tax-options-list') as HTMLElement;
    const $otherTaxExemptionsHeading = $mainSection.querySelector<HTMLElement>('#other-tax-exemptions-heading') as HTMLElement;
    const $addMoreBtn = document.querySelector<HTMLButtonElement>('#add-more') as HTMLButtonElement;

    const $viewMoreBtnParent = template.Clone('#view-more-btn-template').firstElementChild as HTMLElement;
    const $viewMoreBtn = $viewMoreBtnParent?.querySelector('#view-more-btn') as HTMLButtonElement;

    const $currMax = $viewMoreBtnParent?.querySelector('#current-max') as HTMLElement;
    const $totalResults = $viewMoreBtnParent?.querySelector('#total-tab-results') as HTMLElement;

    const userTaxOptions = new Map<string, models.HyejooUserTaxExemption>();

    let pageNumber: number = 0;

    let taxOptionQueue: models.HyejooUserTaxExemption[] = [];
    let defaultTaxOption: models.HyejooUserTaxExemption | undefined;
    let nextPage = true;

    function setup(): PageLoader<models.HyejooUserTaxExemption> {
        const pl: PageLoader<models.HyejooUserTaxExemption> = new PageLoader<models.HyejooUserTaxExemption>({
            clearResults: false,
            parent: $taxOptionsList,
            nextBtn: $viewMoreBtn,
            get: () => getNextTaxExemption().then(taxOptions => {
                if (taxOptions.length === 0) {
                    $taxOptionsListParent.classList.add('d-none');
                    $addMoreBtn.classList.add('text-center');
                } else {
                    $taxOptionsListParent.classList.remove('d-none');
                    $addMoreBtn.classList.remove('text-center');
                }
                return { rows: taxOptions, nextPage: taxOptionQueue.length > 0 };
            }),
            draw: ute => {
                if (ute.id) {
                    userTaxOptions.set(ute.id, ute);
                }
                return taxoptions.Draw(ute, {
                    el: template.Clone('#user-tax-exemption-template').firstElementChild as HTMLElement,
                    radio: false,
                    onSetDefault: setDefault,
                }).then($ute => {
                    const $edit = $ute.querySelector<HTMLButtonElement>('.user-tax-exemption-edit');
                    $edit?.addEventListener('click', editTaxOption);
                    const $delete = $ute.querySelector<HTMLButtonElement>('.user-tax-exemption-delete');
                    $delete?.addEventListener('click', e => promptDeleteTaxOption(e, false));
                    return $ute;
                });
            },
            afterLoad: () => {
                if (taxOptionQueue.length > 0) {
                    $taxOptionsList?.appendChild($viewMoreBtnParent);
                } else {
                    $viewMoreBtnParent?.remove();
                }

                $currMax.textContent = String((pl.currentPage() + 1) * 5);
            },
        });
        return pl;
    }

    const pl = setup();

    async function getNextTaxExemption(): Promise<models.HyejooUserTaxExemption[]> {
        if (taxOptionQueue.length <= 6 && nextPage) {
            await fetchTaxExemption();
        }

        return new Promise(resolve => {
            const out: models.HyejooUserTaxExemption[] = [];
            if (taxOptionQueue.length) {
                for (let i = 0; i < 5; i++) {
                    const ute = taxOptionQueue.shift();
                    if (ute) {
                        out.push(ute);
                    } else {
                        break;
                    }
                }
            }

            resolve(out);
        });
    }

    async function fetchTaxExemption(): Promise<void> {
        const data = await UserTaxExemptions({
            sort: 'default,created',
            max: 10,
            p: pageNumber || 0,
        });

        if (!pageNumber) {
            pageNumber = 0;
        }
        if (pageNumber === 0) {
            defaultTaxOption = undefined;
        }
        taxOptionQueue.push(...data.userTaxExemptions);
        nextPage = data.nextPage;
        pageNumber++;
        if (taxOptionQueue[0]?.default) {
            defaultTaxOption = taxOptionQueue.shift();
        }
    }

    const getTaxOptionCount = async () => await axios.get('/user-tax-exemptions', {
        params: {
            // exclude default option from this count since the pageloader only handles non-default options
            'q': '-label:default',
            'colStats': 'id',
        },
    }).then(resp => models.ColumnStatsResponse.fromJSON(resp.data).count);

    async function reloadTaxOptions(toPage: number = 1) {
        $defaultTaxOption.innerHTML = '';

        userTaxOptions.clear();
        taxOptionQueue = [];
        QueryString().delete('p');
        pageNumber = 0;
        $taxOptionsList.innerHTML = '';

        await fetchTaxExemption();

        if (taxOptionQueue.length === 0 && pageNumber === 1 && !defaultTaxOption) {
            document.querySelector('#no-tax-options')?.classList.remove('d-none');
        }

        if (defaultTaxOption) {
            $defaultTaxOptionParent.classList.remove('d-none');
            $otherTaxExemptionsHeading.classList.remove('d-none');
            userTaxOptions.set(defaultTaxOption.id, defaultTaxOption);
            $defaultTaxOption.innerHTML = '';
            const $ute = await taxoptions.Draw(defaultTaxOption, { radio: false }).then($ute => {
                const $edit = $ute.querySelector<HTMLButtonElement>('.user-tax-exemption-edit');
                $edit?.addEventListener('click', editTaxOption);
                const $delete = $ute.querySelector<HTMLButtonElement>('.user-tax-exemption-delete');
                $delete?.addEventListener('click', e => promptDeleteTaxOption(e, true));
                return $ute;
            });
            $defaultTaxOption.appendChild($ute);
        } else {
            $defaultTaxOptionParent.classList.add('d-none');
            $otherTaxExemptionsHeading.classList.add('d-none');
        }

        $totalResults.textContent = await getTaxOptionCount().then(count => String(count));

        pl.load();

        if (toPage > 1) {
            void reloadTaxOptions(toPage - 1);
        }
    }

    await reloadTaxOptions();

    const setDefault = async (uteInit: models.IUserTaxExemption) => {
        const ute = new models.UserTaxExemption(uteInit);
        await UpdateUserTaxExemptionLabel(ute.id, 'default').
            then(resp => {
                if (resp) {
                    toast.Success('Default tax exemption updated');
                } else {
                    toast.Error('There was an error updating your default tax exemption');
                }
            }).
            catch(ShowError).
            finally(() => {
                setTimeout(async () => await reloadTaxOptions(pl.currentPage() + 1));
            });
    };

    $addMoreBtn.addEventListener('click', () => {
        taxoptions.PromptNew(sess.user).then(() => {
            void reloadTaxOptions();
        }).catch(ShowError);
    });

    function editTaxOption(e: Event) {
        if (e.target instanceof HTMLButtonElement && e.target.classList.contains('user-tax-exemption-edit')) {
            const $ute = e.target.closest('.user-tax-exemption') as HTMLElement;
            const id = $ute.getAttribute('data-user-tax-exemption-id') || '';
            const ute = userTaxOptions.get(id);
            if (ute) {
                taxoptions.PromptEdit(sess.user, ute).then(() => {
                    void reloadTaxOptions(pl.currentPage() + 1);
                }).catch(ShowError);
            }
        }
    }

    const $deleteConfirmTemplate = document.querySelector('.delete-confirm-template') as HTMLTemplateElement;
    function promptDeleteTaxOption(e: Event, isDefault: boolean) {
        if (e.target instanceof HTMLButtonElement && e.target.classList.contains('user-tax-exemption-delete')) {
            const $ute = e.target.closest('.user-tax-exemption') as HTMLElement;
            const id = $ute.getAttribute('data-user-tax-exemption-id') || '';

            const deleteTooltip = new bs.Tooltip(e.target, {
                trigger: 'manual',
                placement: 'top',
                title: 'Are you sure you want to delete this tax exemption?',
                html: true,
                offset: [0, 16],
            });

            const $deleteConfirm = template.Clone($deleteConfirmTemplate).firstElementChild as HTMLElement;

            const oldDefault = defaultTaxOption;

            const deleteTaxOption = async (id: string) => {
                await axios.delete(`/user-tax-exemptions/${id}`).then(() => {
                    toast.Undo('Deleted tax exemption.', async () => {
                        await restoreTaxOption(id);
                    });
                    if (isDefault) {
                        defaultTaxOption = undefined;
                    }

                    void reloadTaxOptions(pl.currentPage() + 1);
                    deleteTooltip?.dispose();
                });
            };

            const restoreTaxOption = async (id: string) => {
                await axios.patch(`/user-tax-exemptions/${id}`, {
                    'active': true,
                }).then(() => {
                    toast.Undo('Restored tax exemption.', async () => {
                        await deleteTaxOption(id);
                    });
                    if (isDefault) {
                        defaultTaxOption = oldDefault;
                    }

                    void reloadTaxOptions(pl.currentPage() + 1);
                });
            };

            const $confirm = $deleteConfirm.querySelector('.delete-confirm-button') as HTMLButtonElement;
            $confirm.addEventListener('click', async () => {
                await deleteTaxOption(id);
            });

            const $cancel = $deleteConfirm.querySelector('.delete-cancel-button') as HTMLButtonElement;
            $cancel.addEventListener('click', () => {
                deleteTooltip?.dispose();
            });

            deleteTooltip.setContent({ '.tooltip-inner': $deleteConfirm });
            deleteTooltip.show();
        }
    }
}
